import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { database, ref, onValue, off, remove } from '../lib/firebase';
import { useAuthStore } from '../stores/useAuthStore';
import { GAMES } from '../games/registry';
import { Swords, X } from 'lucide-react';

interface Invite {
  id: string;
  gameId: string;
  gameType: string;
  fromUsername: string;
  fromAuthId: string;
  createdAt: number;
}

export default function InviteNotification() {
  const user = useAuthStore(state => state.user);
  const [invites, setInvites] = useState<Invite[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.uid) return;

    const invitesRef = ref(database, `invites/${user.uid}`);
    onValue(invitesRef, (snapshot) => {
      if (!snapshot.exists()) {
        setInvites([]);
        return;
      }
      const data = snapshot.val();
      const list: Invite[] = Object.entries(data).map(([id, val]: [string, any]) => ({
        id,
        ...val,
      }));
      // Only show invites from the last 2 minutes
      const recent = list.filter(i => Date.now() - i.createdAt < 120000);
      setInvites(recent);
    });

    return () => off(invitesRef);
  }, [user?.uid]);

  const handleAccept = (invite: Invite) => {
    // Navigate to play page with invite params
    navigate(`/play?invite=${invite.gameId}&game=${invite.gameType}`);
    // Remove invite
    if (user?.uid) {
      remove(ref(database, `invites/${user.uid}/${invite.id}`));
    }
    setInvites(prev => prev.filter(i => i.id !== invite.id));
  };

  const handleDismiss = (invite: Invite) => {
    if (user?.uid) {
      remove(ref(database, `invites/${user.uid}/${invite.id}`));
    }
    setInvites(prev => prev.filter(i => i.id !== invite.id));
  };

  if (invites.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2">
      {invites.map(invite => {
        const game = GAMES.find(g => g.id === invite.gameType);
        return (
          <div
            key={invite.id}
            className="bg-slate-800/95 backdrop-blur-lg border border-pink-500/30 rounded-xl p-4 w-72 shadow-xl animate-in slide-in-from-right"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Swords className="w-4 h-4 text-pink-400" />
                <span className="font-medium text-sm">Game Challenge!</span>
              </div>
              <button onClick={() => handleDismiss(invite)} className="text-gray-300 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-gray-300 mb-3">
              <span className="text-white font-medium">{invite.fromUsername}</span> wants to play{' '}
              <span className="text-pink-400">{game?.name || invite.gameType}</span>
            </p>
            <button
              onClick={() => handleAccept(invite)}
              className="w-full px-3 py-2 bg-gradient-to-r from-pink-600 to-violet-600 rounded-lg text-sm font-medium hover:opacity-90 transition"
            >
              Accept Challenge
            </button>
          </div>
        );
      })}
    </div>
  );
}
